// PRÉFÉRENCES D'APPRENTISSAGE du player Reps.
//
// Deux curseurs, demandés le jour où l'on a montré la réponse à l'échec :
// certains veulent la voir tout de suite, d'autres veulent chercher ; certains
// veulent passer après deux échecs, d'autres veulent que l'exercice revienne
// jusqu'à la victoire. Ce n'est pas à nous de trancher pour tout le monde.
//
//   showAnswers        — la solution est proposée dès la première erreur.
//   insistUntilCorrect — un exercice raté revient en file jusqu'à la réussite
//                        (sinon il quitte la file après deux échecs).
//   readAlong          — quand BeBot lit un écran de concept, les mots
//                        s'allument au rythme de sa voix (karaoké de lecture).
//
// Persistance à DEUX étages : le localStorage répond tout de suite (et reste
// seul maître pour l'invité), la BASE fait foi pour un compte connecté — le
// réglage activé sur l'app mobile vaut sur le web, et inversement. Le player
// hydrate depuis le cache puis rattrape la vérité du compte (fetch), et chaque
// bascule pousse son changement au serveur (push, fire-and-forget).
// L'app mobile a son miroir : reps-app/src/state/learning-prefs.ts.

const KEY = "reps-learning-prefs";

// VERSION du cache local. Elle n'existe que pour une raison : le jour où l'on
// change un DÉFAUT, le cache déjà écrit chez l'apprenant mentirait. Il porte la
// valeur de l'ANCIEN défaut, que personne n'a choisie, et cette valeur écraserait
// silencieusement le nouveau défaut — l'apprenant qui a un compte depuis
// longtemps n'aurait jamais la nouvelle règle. À chaque changement de défaut on
// incrémente donc VERSION et on OUBLIE la clé concernée : elle repart du défaut,
// tandis que les réglages vraiment choisis (ceux dont le défaut n'a pas bougé)
// restent intacts. Le cache est réestampillé à la première écriture.
//
//   → v2 : insistUntilCorrect passe de false à true. Tout cache antérieur (le
//          premier format n'avait aucun champ v : son absence suffit à le
//          reconnaître) voit sa valeur d'insistUntilCorrect abandonnée ;
//          showAnswers, dont le défaut n'a pas changé, est conservé tel quel.
const VERSION = 2;

// Clés dont le défaut a changé en même temps que VERSION : leur valeur en cache
// est ignorée quand le cache est plus ancien. Pas de liste par version, une
// seule marche à franchir à la fois — au prochain changement de défaut, on
// remplace le contenu de cette liste.
const RESET_ON_UPGRADE = ["insistUntilCorrect"];

export const DEFAULT_LEARNING_PREFS = {
    // false = le bouton « Voir la solution » n'apparaît qu'au DEUXIÈME échec :
    // on pousse à chercher d'abord. true = proposé dès la première erreur.
    showAnswers: false,
    // true = un exercice raté revient en file jusqu'à la réussite. C'est le
    // défaut voulu : on n'abandonne pas un exercice sur un échec, on insiste
    // jusqu'à la bonne réponse. Réglable dans les préférences du player.
    insistUntilCorrect: true,
    // true = suivi de lecture : les mots s'allument pendant que BeBot les lit.
    // Nouveau défaut (clé inédite), donc AUCUN bump de VERSION : un vieux cache
    // sans la clé retombe naturellement sur true.
    readAlong: true,
};

export function getLearningPrefs() {
    try {
        const raw = window.localStorage.getItem(KEY);
        if (!raw) return { ...DEFAULT_LEARNING_PREFS };
        const parsed = JSON.parse(raw);
        // Cache écrit avant un changement de défaut : les clés listées y valent
        // l'ancien défaut, on les traite comme absentes.
        const stale = parsed.v !== VERSION;
        const read = (key) => {
            if (stale && RESET_ON_UPGRADE.includes(key)) {
                return DEFAULT_LEARNING_PREFS[key];
            }
            return typeof parsed[key] === "boolean"
                ? parsed[key]
                : DEFAULT_LEARNING_PREFS[key];
        };
        return {
            showAnswers: read("showAnswers"),
            insistUntilCorrect: read("insistUntilCorrect"),
            readAlong: read("readAlong"),
        };
    } catch (e) {
        return { ...DEFAULT_LEARNING_PREFS };
    }
}

export function saveLearningPrefs(prefs) {
    try {
        // On estampille la version : ce cache-ci porte des valeurs à jour, il ne
        // sera plus rejoué contre le défaut au prochain démarrage.
        window.localStorage.setItem(KEY, JSON.stringify({ ...prefs, v: VERSION }));
    } catch (e) {
        /* localStorage indisponible : la préférence vaut pour la session */
    }
}

/**
 * La vérité du COMPTE. Retourne les préférences serveur (et met le cache
 * local à jour), ou null quand il n'y a rien à en tirer : invité (401),
 * panne, hors ligne — dans ces cas le localStorage reste la référence.
 */
export async function fetchLearningPrefs() {
    try {
        const res = await fetch("/api/reps/learning-prefs");
        if (!res.ok) return null;
        const data = await res.json();
        const prefs = {
            showAnswers: !!data.showAnswers,
            insistUntilCorrect: !!data.insistUntilCorrect,
            // Clé absente (back pas encore déployé) = défaut, jamais false :
            // sinon un déploiement décalé éteindrait le réglage chez tout le
            // monde et l'écrirait en cache comme un choix.
            readAlong:
                typeof data.readAlong === "boolean"
                    ? data.readAlong
                    : DEFAULT_LEARNING_PREFS.readAlong,
        };
        saveLearningPrefs(prefs);
        return prefs;
    } catch (e) {
        return null;
    }
}

/**
 * Pousse UN changement au compte (clés présentes seulement), sans attendre
 * ni échouer bruyamment : pour l'invité le serveur répond 401 et le réglage
 * reste local, ce qui est exactement le comportement voulu.
 */
export function pushLearningPrefs(patch) {
    try {
        fetch("/api/reps/learning-prefs", {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify(patch),
        }).catch(() => {});
    } catch (e) {
        /* fetch indisponible (SSR) : rien à pousser */
    }
}
